import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowDownToLine,
  BadgeCheck,
  Banknote,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Filter,
  Loader2,
  PackageCheck,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Smartphone,
  Split,
  Store,
  UsersRound,
  WalletCards,
  Gauge,
  ShieldAlert,
  TimerReset,
  UserCog,
  RotateCcw,
  Building2,
  UtensilsCrossed,
  Save,
  X,
  Info,
} from "lucide-react";
import { paymentCollectionsService } from "../../services/paymentCollections.service";
import { driverFinancialControlsService } from "../../services/driverFinancialControls.service";
import { settingsService } from "../../services/settings.service";
import { restaurantAdminService } from "../../services/restaurantAdmin.service";
import { hasPermission } from "../AdminAuth/components/authStore";
import { useAppPopup } from "../../components/AppPopup/AppPopupProvider";

const PAYMENT_COLLECTION_TABS = [
  ["overview", "Overview"],
  ["collections", "Collections"],
  ["drivers", "Drivers"],
  ["settlements", "Settlements"],
  ["setup", "Collection setup"],
];

const ADMIN_FINANCIAL_TAB = ["financial", "Driver financial controls"];


function FilterDropdown({
  value,
  onChange,
  options = [],
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0] ||
    { value: "", label: "Select" };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[44px] w-full items-center justify-between rounded-[12px] border bg-white px-3 text-left text-[12px] font-semibold outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d7db]"
            : "border-[#f2c6cc] text-slate-600 hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-slate-600">
          {selected?.label}
        </span>
        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[50px] z-[200] max-h-[260px] overflow-y-auto rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={String(option.value || "all")}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[10px] px-3 py-2.5 text-left text-[12px] transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function n(value) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}
function money(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n(value));
}
function shortMoney(value) {
  const amount = n(value);
  if (Math.abs(amount) >= 10000000)
    return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return money(amount);
}
function dateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}
function dateOnly(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}
function pick(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return null;
}
function labelize(value) {
  return String(value || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function randomKey() {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }
  return `settlement-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
function todayString() {
  return new Date().toISOString().slice(0, 10);
}
function toLocalDateTimeInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDateTimeInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
function futureIso(minutes) {
  return new Date(Date.now() + Number(minutes || 0) * 60000).toISOString();
}
function endOfTodayIso() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}
function warehouseRowsOf(res) {
  return res?.warehouses || res?.data?.warehouses || res?.items || res?.data?.items || [];
}
function restaurantRowsOf(res) {
  return res?.rows || res?.data?.rows || res?.restaurants || res?.data?.restaurants || res?.items || res?.data?.items || [];
}
function rangePreset(name) {
  const now = new Date();
  const to = todayString();
  const fromDate = new Date(now);
  if (name === "today") return { from: to, to };
  if (name === "7d") fromDate.setDate(now.getDate() - 6);
  if (name === "30d") fromDate.setDate(now.getDate() - 29);
  if (name === "month") fromDate.setDate(1);
  return { from: fromDate.toISOString().slice(0, 10), to };
}
function readDriver(row) {
  const driver = row?.driver || {};
  return {
    id:
      pick(row, "driverId", "driver_id") ||
      driver?.id ||
      driver?.partnerId ||
      "",
    name: driver?.name || driver?.fullName || null,
    phone: driver?.phone || null,
    partnerId: driver?.partnerId || driver?.partner_id || null,
  };
}
function driverTitle(row) {
  const d = readDriver(row);
  return (
    d.name ||
    d.partnerId ||
    (d.id ? `Driver ${String(d.id).slice(0, 8)}` : "Driver")
  );
}
function collectionValue(row, camel, snake) {
  return pick(row, camel, snake);
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-rose-200 bg-rose-50 text-rose-700",
    blue: "border-sky-200 bg-sky-50 text-sky-700",
    pink: "border-[#f2c6cc] bg-[#fff6f7] text-[#c65d6c]",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-extrabold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}
function statusTone(status) {
  const s = String(status || "").toUpperCase();
  if (["PAID", "COLLECTED", "SETTLED", "VERIFIED"].includes(s)) return "green";
  if (["PENDING", "DUE", "PARTIALLY_SETTLED", "ONLINE_QR_ACTIVE"].includes(s))
    return "amber";
  if (["REJECTED", "CANCELLED", "PAYMENT_REVIEW_REQUIRED"].includes(s))
    return "red";
  return "slate";
}
function MethodPill({ mode }) {
  const v = String(mode || "").toUpperCase();
  if (v === "COMPANY_RAZORPAY")
    return <Pill tone="blue">Company Razorpay</Pill>;
  if (v === "DRIVER_CASH") return <Pill tone="green">Driver Cash</Pill>;
  if (v === "DRIVER_SPLIT") return <Pill tone="pink">Cash + Driver UPI</Pill>;
  return <Pill>{labelize(mode)}</Pill>;
}
function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[22px] border border-slate-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}
function StatCard({ icon: Icon, label, value, note, tone = "pink" }) {
  const tones = {
    pink: "bg-[#fff1f3] text-[#d86a78]",
    green: "bg-emerald-50 text-emerald-600",
    blue: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <Card className="min-w-0 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>
          <div
            className="mt-2 break-words text-[clamp(20px,2vw,30px)] font-black tracking-[-0.04em] text-slate-900"
            title={String(value)}
          >
            {value}
          </div>
          {note ? (
            <p className="mt-2 text-xs font-medium leading-5 text-slate-500">
              {note}
            </p>
          ) : null}
        </div>
        <div
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${tones[tone] || tones.pink}`}
        >
          <Icon size={20} />
        </div>
      </div>
    </Card>
  );
}
function InfoTip({ text, className = "" }) {
  if (!text) return null;

  return (
    <span className={`group relative inline-flex shrink-0 ${className}`}>
      <span
        className="grid h-[18px] w-[18px] place-items-center rounded-full border border-slate-200 bg-white text-slate-400 transition hover:border-rose-200 hover:text-rose-500"
        aria-hidden="true"
      >
        <Info size={12} />
      </span>
      <span className="pointer-events-none absolute left-1/2 top-6 z-40 hidden w-[260px] -translate-x-1/2 rounded-[12px] border border-slate-200 bg-slate-900 px-3 py-2 text-[11px] font-medium leading-5 text-white shadow-xl group-hover:block">
        {text}
      </span>
    </span>
  );
}

function Switch({ checked, onChange, disabled = false, label, description, info }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`flex w-full items-center justify-between gap-4 rounded-2xl border p-4 text-left transition ${disabled ? "cursor-not-allowed bg-slate-50 opacity-60" : "bg-white hover:border-[#e8a7b0]"}`}
      style={{ borderColor: checked ? "#efb6be" : "#e2e8f0" }}
    >
      <span className="min-w-0">
        <span className="flex items-start gap-2">
          <span className="block text-sm font-extrabold text-slate-800">
            {label}
          </span>
          <InfoTip text={info} className="mt-0.5" />
        </span>
        {description ? (
          <span className="mt-1 block text-[11px] leading-5 text-slate-500">
            {description}
          </span>
        ) : null}
      </span>
      <span
        className={`relative h-7 w-12 shrink-0 rounded-full transition ${checked ? "bg-[#d86a78]" : "bg-slate-300"}`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-6" : "left-1"}`}
        />
      </span>
    </button>
  );
}
function Modal({ open, title, subtitle, onClose, children, wide = false }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1600] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[2px]"
      onMouseDown={onClose}
    >
      <div
        className={`max-h-[90vh] w-full overflow-auto rounded-[26px] bg-white shadow-2xl ${wide ? "max-w-4xl" : "max-w-xl"}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white/95 p-5 backdrop-blur">
          <div>
            <h3 className="text-xl font-black text-slate-900">{title}</h3>
            {subtitle ? (
              <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function Pagination({ page, totalPages, total, limit, onPage, onLimit }) {
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages || 1, start + 4);
  for (let i = start; i <= end; i += 1) pages.push(i);
  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
      <div className="text-xs font-semibold text-slate-500">
        {total || 0} records · {limit} per page
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold"
          value={limit}
          onChange={(e) => onLimit(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((v) => (
            <option key={v} value={v}>
              {v} rows
            </option>
          ))}
        </select>
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 disabled:opacity-40"
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`h-9 min-w-9 rounded-xl px-3 text-xs font-extrabold ${p === page ? "bg-[#d86a78] text-white" : "border border-slate-200 bg-white text-slate-600"}`}
          >
            {p}
          </button>
        ))}
        <button
          disabled={page >= (totalPages || 1)}
          onClick={() => onPage(page + 1)}
          className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 disabled:opacity-40"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

export default function PaymentCollectionsPage({ scope = "admin" }) {
  const isWarehouse = scope === "warehouse";
  const api = isWarehouse
    ? paymentCollectionsService.warehouse
    : paymentCollectionsService.admin;
  const canSettle = isWarehouse
    ? hasPermission(["wh.payments.settle"])
    : hasPermission(["admin.payment_collections.settle"]);
  const canVerify = isWarehouse
    ? hasPermission(["wh.payments.verify"])
    : hasPermission(["admin.payment_collections.verify"]);
  const canSettings = isWarehouse
    ? hasPermission(["wh.payment_settings.update"])
    : hasPermission(["admin.payment_collection_settings.update"]);
  const canFinancialRead = !isWarehouse && hasPermission(["admin.driver_financial_controls.read"]);
  const canFinancialEdit = !isWarehouse && hasPermission(["admin.driver_financial_controls.update"]);
  const paymentTabs = useMemo(
    () => (isWarehouse || !canFinancialRead
      ? PAYMENT_COLLECTION_TABS
      : [
          ...PAYMENT_COLLECTION_TABS.slice(0, 3),
          ADMIN_FINANCIAL_TAB,
          ...PAYMENT_COLLECTION_TABS.slice(3),
        ]),
    [isWarehouse, canFinancialRead],
  );

  const [tab, setTab] = useState("overview");
  const [preset, setPreset] = useState("30d");
  const [filters, setFilters] = useState(() => ({
    ...rangePreset("30d"),
    orderType: "",
    mode: "",
    settlementStatus: "",
    q: "",
    reviewRequired: "",
  }));
  const [summary, setSummary] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [collections, setCollections] = useState({
    rows: [],
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [settlements, setSettlements] = useState({
    rows: [],
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [collectionPage, setCollectionPage] = useState(1);
  const [collectionLimit, setCollectionLimit] = useState(25);
  const [settlementPage, setSettlementPage] = useState(1);
  const [settlementLimit, setSettlementLimit] = useState(25);
  const [detail, setDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [settlementDetail, setSettlementDetail] = useState(null);
  const [settlementModal, setSettlementModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [financialDrivers, setFinancialDrivers] = useState({ rows: [], page: 1, limit: 25, total: 0, totalPages: 1 });
  const [financialPage, setFinancialPage] = useState(1);
  const [financialLimit, setFinancialLimit] = useState(25);
  const [financialQ, setFinancialQ] = useState("");
  const [financialSearchInput, setFinancialSearchInput] = useState("");
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialDetail, setFinancialDetail] = useState(null);
  const [financialDetailLoading, setFinancialDetailLoading] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const popup = useAppPopup();

  const queryFilters = useMemo(() => {
    const base = {
      from: filters.from,
      to: filters.to,
      mode: filters.mode,
      settlementStatus: filters.settlementStatus,
      q: filters.q,
      reviewRequired: filters.reviewRequired,
    };
    if (!isWarehouse) base.orderType = filters.orderType;
    return base;
  }, [filters, isWarehouse]);

  const loadCore = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, driverRes] = await Promise.all([
        api.getSummary(queryFilters),
        api.getDrivers(queryFilters),
      ]);
      setSummary(summaryRes?.summary || {});
      setDrivers(Array.isArray(driverRes?.rows) ? driverRes.rows : []);
    } catch (e) {
      setError(e?.message || "Failed to load payment collections");
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    setTableLoading(true);
    try {
      const res = await api.getCollections({
        ...queryFilters,
        page: collectionPage,
        limit: collectionLimit,
      });
      setCollections({
        rows: res?.rows || [],
        page: res?.page || collectionPage,
        limit: res?.limit || collectionLimit,
        total: res?.total || 0,
        totalPages: res?.totalPages || 1,
      });
    } catch (e) {
      setError(e?.message || "Failed to load collections");
    } finally {
      setTableLoading(false);
    }
  };

  const loadSettlements = async () => {
    setTableLoading(true);
    try {
      const res = await api.getSettlements({
        driverId: queryFilters.driverId,
        from: queryFilters.from,
        to: queryFilters.to,
        page: settlementPage,
        limit: settlementLimit,
      });
      setSettlements({
        rows: res?.rows || [],
        page: res?.page || settlementPage,
        limit: res?.limit || settlementLimit,
        total: res?.total || 0,
        totalPages: res?.totalPages || 1,
      });
    } catch (e) {
      setError(e?.message || "Failed to load settlements");
    } finally {
      setTableLoading(false);
    }
  };

  const loadSettings = async () => {
    setTableLoading(true);
    try {
      const res = await api.getSettings();
      setSettings(res?.data || res || null);
    } catch (e) {
      setError(e?.message || "Failed to load payment collection settings");
    } finally {
      setTableLoading(false);
    }
  };

  const loadSetupOptions = async () => {
    if (isWarehouse) return;
    const [warehouseRes, restaurantRes] = await Promise.allSettled([
      settingsService.getWarehouseFilterOptions({ status: "active" }),
      restaurantAdminService.listRestaurants({ page: 1, limit: 500 }),
    ]);
    if (warehouseRes.status === "fulfilled") {
      setWarehouseOptions(warehouseRowsOf(warehouseRes.value).map((item) => ({
        id: item.id || item._id || item.warehouseId || item.warehouse_id,
        name: item.name || item.warehouseName || item.warehouse_name || item.code || item.id,
        code: item.code || "",
        city: item.city || "",
      })).filter((item) => item.id));
    }
    if (restaurantRes.status === "fulfilled") {
      setRestaurantOptions(restaurantRowsOf(restaurantRes.value).map((item) => ({
        id: item.id || item._id || item.restaurantId || item.restaurant_id,
        name: item.name || item.restaurantName || item.restaurant_name || item.businessName || item.id,
        city: item.city || item.address?.city || item.location?.city || "",
      })).filter((item) => item.id));
    }
  };

  const loadFinancialDrivers = async () => {
    if (!canFinancialRead) return;
    setFinancialLoading(true);
    try {
      const res = await driverFinancialControlsService.list({
        page: financialPage,
        limit: financialLimit,
        q: financialQ.trim() || undefined,
      });
      setFinancialDrivers({
        rows: Array.isArray(res?.rows) ? res.rows : [],
        page: res?.page || financialPage,
        limit: res?.limit || financialLimit,
        total: res?.total || 0,
        totalPages: res?.totalPages || 1,
      });
    } catch (e) {
      setError(e?.message || "Failed to load driver financial controls");
    } finally {
      setFinancialLoading(false);
    }
  };

  const openFinancialDriver = async (driverId) => {
    if (!driverId) return;
    setFinancialDetailLoading(true);
    setFinancialDetail({ loading: true, driver: { id: driverId } });
    try {
      const res = await driverFinancialControlsService.get(driverId);
      setFinancialDetail(res || null);
    } catch (e) {
      setFinancialDetail(null);
      setError(e?.message || "Failed to load driver financial details");
    } finally {
      setFinancialDetailLoading(false);
    }
  };

  useEffect(() => {
    loadCore();
  }, [queryFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "collections") loadCollections();
  }, [tab, queryFilters, collectionPage, collectionLimit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "settlements") loadSettlements();
  }, [tab, queryFilters, settlementPage, settlementLimit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "setup") {
      loadSettings();
      loadSetupOptions();
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab !== "financial") return undefined;
    const timer = window.setTimeout(() => {
      setFinancialPage(1);
      setFinancialQ(financialSearchInput.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [tab, financialSearchInput]);
  useEffect(() => {
    if (tab === "financial") loadFinancialDrivers();
  }, [tab, financialPage, financialLimit, financialQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setNotice("");
    await loadCore();
    if (tab === "collections") await loadCollections();
    if (tab === "settlements") await loadSettlements();
    if (tab === "financial") await loadFinancialDrivers();
    if (tab === "setup") await loadSettings();
  };

  const setRange = (name) => {
    setPreset(name);
    if (name !== "custom") setFilters((f) => ({ ...f, ...rangePreset(name) }));
    setCollectionPage(1);
    setSettlementPage(1);
  };

  const openCollection = async (row) => {
    const id = pick(row, "id");
    if (!id) return;
    setDetail(row);
    setEvents([]);
    try {
      const res = await api.getCollectionEvents(id, { limit: 150 });
      setDetail({ ...row, ...(res?.collection || {}) });
      setEvents(Array.isArray(res?.events) ? res.events : []);
    } catch (e) {
      setError(e?.message || "Failed to load collection timeline");
    }
  };

  const openSettlement = async (row) => {
    const id = row?.id;
    if (!id) return;
    setSettlementDetail({ settlement: row, allocations: [] });
    try {
      const res = await api.getSettlement(id);
      setSettlementDetail({
        ...res,
        driver: res?.driver || row?.driver || null,
      });
    } catch (e) {
      setError(e?.message || "Failed to load settlement details");
    }
  };

  const createSettlement = async (form) => {
    setBusy(true);
    setError("");
    try {
      await api.createSettlement({
        idempotencyKey: randomKey(),
        driverId: form.driverId,
        amount: Number(form.amount),
        settlementMethod: form.settlementMethod,
        reference: form.reference || null,
        proofUrl: form.proofUrl || null,
        note: form.note || null,
      });
      setSettlementModal(null);
      setNotice("Settlement recorded successfully.");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to record settlement");
    } finally {
      setBusy(false);
    }
  };

  const verifySettlement = async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      await api.verifySettlement(id);
      setNotice("Settlement verified and allocated to driver collections.");
      setSettlementDetail(null);
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to verify settlement");
    } finally {
      setBusy(false);
    }
  };

  const rejectSettlement = async ({ id, reason }) => {
    setBusy(true);
    try {
      await api.rejectSettlement(id, reason);
      setRejectModal(null);
      setSettlementDetail(null);
      setNotice("Settlement rejected. Reserved amounts were released.");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to reject settlement");
    } finally {
      setBusy(false);
    }
  };

  const resolveReview = async ({ id, note, reference }) => {
    setBusy(true);
    try {
      await paymentCollectionsService.admin.resolveReview(id, {
        note,
        resolutionReference: reference || null,
      });
      setReviewModal(null);
      setDetail(null);
      setNotice(
        "Payment reconciliation marked resolved. No bank movement was performed.",
      );
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to resolve payment review");
    } finally {
      setBusy(false);
    }
  };

  const updateSettings = async (payload) => {
    setBusy(true);
    try {
      const res = await api.updateSettings(payload);
      setSettings(res?.data || res || null);
      setNotice("Payment collection setup saved.");
      void popup.alert("Payment collection settings have been saved successfully.", {
        title: "Setup saved",
        confirmText: "OK",
      });
    } catch (e) {
      setError(e?.message || "Failed to save payment collection setup");
    } finally {
      setBusy(false);
    }
  };

  const updateFinancialDriver = async (driverId, payload) => {
    setBusy(true);
    try {
      await driverFinancialControlsService.update(driverId, payload);
      setNotice("Driver financial policy updated.");
      await Promise.all([loadFinancialDrivers(), openFinancialDriver(driverId)]);
    } catch (e) {
      setError(e?.message || "Failed to update driver financial policy");
    } finally {
      setBusy(false);
    }
  };

  const extendFinancialDeadline = async (driverId, payload) => {
    setBusy(true);
    try {
      await driverFinancialControlsService.extendDeadline(driverId, payload);
      setNotice("Settlement deadline extended.");
      await Promise.all([loadFinancialDrivers(), openFinancialDriver(driverId)]);
    } catch (e) {
      setError(e?.message || "Failed to extend settlement deadline");
    } finally {
      setBusy(false);
    }
  };

  const clearFinancialOverride = async (driverId, reason) => {
    setBusy(true);
    try {
      await driverFinancialControlsService.clearOverride(driverId, { reason: reason || null });
      setNotice("Driver returned to global financial policy.");
      await Promise.all([loadFinancialDrivers(), openFinancialDriver(driverId)]);
    } catch (e) {
      setError(e?.message || "Failed to clear driver override");
    } finally {
      setBusy(false);
    }
  };

  const totalCollected =
    pick(summary, "customer_collected_total", "customerCollectedTotal") || 0;
  const directReceived =
    pick(
      summary,
      "company_direct_received",
      "company_razorpay_received",
      "companyRazorpayReceived",
    ) || 0;
  const driverHeld =
    pick(
      summary,
      "driver_held_collected",
      "driver_collected_total",
      "driverCollectedTotal",
    ) || 0;
  const settled =
    pick(
      summary,
      "driver_settled",
      "driver_settled_total",
      "driverSettledTotal",
    ) || 0;
  const outstanding =
    pick(
      summary,
      "driver_outstanding",
      "driver_outstanding_total",
      "driverOutstandingTotal",
    ) || 0;
  const pendingVerification =
    pick(
      summary,
      "driver_pending_verification",
      "driver_pending_verification_total",
      "driverPendingVerificationTotal",
    ) || 0;
  const reviews =
    pick(
      summary,
      "reconciliation_required",
      "payment_review_required",
      "paymentReviewRequired",
    ) || 0;
  const companyReceived =
    pick(summary, "company_received_total", "companyReceivedTotal") || 0;
  const collectionCount =
    pick(summary, "collected_orders", "collectedOrders") || 0;

  return (
    <div className="space-y-5 pb-12">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="warehouse-page-title">
            PAYMENT COLLECTIONS
          </h1>
        </div>

        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-full bg-[#d86a78] px-5 text-[14px] font-semibold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {tab === "overview" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div
              className="rounded-[18px] px-5 py-4"
              style={{ background: "#fff8f9", border: "1px solid #f2c6cc" }}
            >
              <div className="text-[12px] font-semibold uppercase tracking-wide text-[#d86a78]">
                Customer Paid
              </div>
              <div className="mt-1 text-[30px] font-bold leading-none text-[#d86a78] sm:text-[34px]">
                {shortMoney(totalCollected)}
              </div>
              <div className="mt-2 text-[11px] font-medium text-[#b96a75]">
                {collectionCount} collected POD orders
              </div>
            </div>

            <div
              className="rounded-[18px] px-5 py-4"
              style={{ background: "#f6fff7", border: "1px solid #cfe7d1" }}
            >
              <div className="text-[12px] font-semibold uppercase tracking-wide text-[#69a65f]">
                Company Received
              </div>
              <div className="mt-1 text-[30px] font-bold leading-none text-[#5b9a50] sm:text-[34px]">
                {shortMoney(companyReceived)}
              </div>
              <div className="mt-2 text-[11px] font-medium text-[#6f9b69]">
                Direct Razorpay {shortMoney(directReceived)} + verified settlements
              </div>
            </div>

            <div
              className="rounded-[18px] px-5 py-4"
              style={{ background: "#fffaf0", border: "1px solid #f1dfb8" }}
            >
              <div className="text-[12px] font-semibold uppercase tracking-wide text-[#c58b13]">
                Driver Outstanding
              </div>
              <div className="mt-1 text-[30px] font-bold leading-none text-[#b7791f] sm:text-[34px]">
                {shortMoney(outstanding)}
              </div>
              <div className="mt-2 text-[11px] font-medium text-[#a98238]">
                {shortMoney(pendingVerification)} pending verification
              </div>
            </div>

            <div
              className="rounded-[18px] px-5 py-4"
              style={{ background: n(reviews) ? "#fffaf0" : "#f8fafc", border: n(reviews) ? "1px solid #f1dfb8" : "1px solid #e2e8f0" }}
            >
              <div
                className={`text-[12px] font-semibold uppercase tracking-wide ${n(reviews) ? "text-[#c58b13]" : "text-slate-500"}`}
              >
                Needs Review
              </div>
              <div
                className={`mt-1 text-[30px] font-bold leading-none sm:text-[34px] ${n(reviews) ? "text-[#b7791f]" : "text-slate-700"}`}
              >
                {String(reviews)}
              </div>
              <div className={`mt-2 text-[11px] font-medium ${n(reviews) ? "text-[#a98238]" : "text-slate-400"}`}>
                Late/double payment reconciliation queue
              </div>
            </div>
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
          <span>{error}</span>
          <button onClick={() => setError("")}>
            <X size={17} />
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
          <span className="inline-flex items-center gap-2">
            <Check size={17} />
            {notice}
          </span>
          <button onClick={() => setNotice("")}>
            <X size={17} />
          </button>
        </div>
      ) : null}

      <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {paymentTabs.map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-xs font-black transition ${tab === id ? "bg-[#d86a78] text-white shadow-[0_8px_20px_rgba(216,106,120,0.28)]" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {tab !== "setup" && tab !== "financial" ? (
            <div className="flex flex-wrap gap-2">
              {["today", "7d", "30d", "month", "custom"].map((id) => (
                <button
                  key={id}
                  onClick={() => setRange(id)}
                  className={`rounded-xl border px-3 py-2 text-[11px] font-extrabold ${preset === id ? "border-[#e8a7b0] bg-[#fff3f5] text-[#c65d6c]" : "border-slate-200 bg-white text-slate-500"}`}
                >
                  {
                    {
                      today: "Today",
                      "7d": "7 days",
                      "30d": "30 days",
                      month: "This month",
                      custom: "Custom",
                    }[id]
                  }
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {tab !== "setup" && tab !== "financial" ? (
          <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 md:grid-cols-2 xl:grid-cols-6">
            {preset === "custom" ? (
              <>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, from: e.target.value }));
                    setCollectionPage(1);
                  }}
                  className="h-[44px] rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-semibold text-slate-600 outline-none focus:border-[#d86a78]"
                />
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, to: e.target.value }));
                    setCollectionPage(1);
                  }}
                  className="h-[44px] rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-semibold text-slate-600 outline-none focus:border-[#d86a78]"
                />
              </>
            ) : (
              <div className="col-span-2 flex h-[44px] items-center gap-2 rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-semibold text-slate-500">
                <CalendarDays size={15} />
                {dateOnly(filters.from)} → {dateOnly(filters.to)}
              </div>
            )}
            {!isWarehouse ? (
              <FilterDropdown
                value={filters.orderType}
                onChange={(value) => {
                  setFilters((f) => ({ ...f, orderType: value }));
                  setCollectionPage(1);
                }}
                options={[
                  { value: "", label: "Grocery + Food" },
                  { value: "GROCERY", label: "Grocery" },
                  { value: "FOOD", label: "Food" },
                ]}
              />
            ) : null}

            <FilterDropdown
              value={filters.mode}
              onChange={(value) => {
                setFilters((f) => ({ ...f, mode: value }));
                setCollectionPage(1);
              }}
              options={[
                { value: "", label: "All methods" },
                { value: "COMPANY_RAZORPAY", label: "Company Razorpay" },
                { value: "DRIVER_CASH", label: "Driver Cash" },
                { value: "DRIVER_SPLIT", label: "Cash + Driver UPI" },
              ]}
            />

            <FilterDropdown
              value={filters.settlementStatus}
              onChange={(value) => {
                setFilters((f) => ({ ...f, settlementStatus: value }));
                setCollectionPage(1);
              }}
              options={[
                { value: "", label: "All settlement states" },
                { value: "DUE", label: "Due" },
                { value: "PARTIALLY_SETTLED", label: "Partially settled" },
                { value: "SETTLED", label: "Settled" },
                { value: "NOT_APPLICABLE", label: "Not applicable" },
              ]}
            />
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={filters.q}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, q: e.target.value }));
                  setCollectionPage(1);
                }}
                placeholder="Order / UPI ref"
                className="h-[44px] w-full rounded-[12px] border border-[#f2c6cc] bg-white pl-9 pr-3 text-[12px] font-semibold text-slate-600 outline-none focus:border-[#d86a78]"
              />
            </div>
          </div>
        ) : null}
      </div>

      {tab === "overview" ? (
        <Overview
          loading={loading}
          summary={summary}
          drivers={drivers}
          metrics={{
            totalCollected,
            directReceived,
            driverHeld,
            settled,
            outstanding,
            pendingVerification,
            reviews,
            companyReceived,
          }}
          onOpenDriver={(row) => setSettlementModal({ driver: row })}
          canSettle={canSettle}
          isWarehouse={isWarehouse}
          goCollections={() => setTab("collections")}
        />
      ) : null}

      {tab === "collections" ? (
        <CollectionsTable
          loading={tableLoading}
          data={collections}
          onOpen={openCollection}
          page={collectionPage}
          limit={collectionLimit}
          setPage={setCollectionPage}
          setLimit={(v) => {
            setCollectionLimit(v);
            setCollectionPage(1);
          }}
        />
      ) : null}

      {tab === "drivers" ? (
        <DriversTable
          rows={drivers}
          loading={loading}
          canSettle={canSettle}
          isWarehouse={isWarehouse}
          onSettle={(row) => setSettlementModal({ driver: row })}
        />
      ) : null}

      {tab === "financial" && canFinancialRead ? (
        <FinancialControlsPanel
          data={financialDrivers}
          loading={financialLoading}
          q={financialSearchInput}
          setQ={setFinancialSearchInput}
          page={financialPage}
          limit={financialLimit}
          setPage={setFinancialPage}
          setLimit={(value) => { setFinancialLimit(value); setFinancialPage(1); }}
          onOpen={(row) => openFinancialDriver(row?.id || row?.driverId)}
        />
      ) : null}

      {tab === "settlements" ? (
        <SettlementsTable
          data={settlements}
          loading={tableLoading}
          onOpen={openSettlement}
          onCreate={() => setSettlementModal({ driver: null })}
          canSettle={canSettle}
          page={settlementPage}
          limit={settlementLimit}
          setPage={setSettlementPage}
          setLimit={(v) => {
            setSettlementLimit(v);
            setSettlementPage(1);
          }}
        />
      ) : null}

      {tab === "setup" ? (
        <SetupPanel
          scope={scope}
          settings={settings}
          loading={tableLoading}
          busy={busy}
          canEdit={canSettings}
          warehouseOptions={warehouseOptions}
          restaurantOptions={restaurantOptions}
          onSave={updateSettings}
        />
      ) : null}

      <FinancialDriverModal
        data={financialDetail}
        loading={financialDetailLoading}
        busy={busy}
        canEdit={canFinancialEdit}
        onClose={() => setFinancialDetail(null)}
        onSave={updateFinancialDriver}
        onExtend={extendFinancialDeadline}
        onClear={clearFinancialOverride}
      />
      <CollectionDetailModal
        row={detail}
        events={events}
        onClose={() => setDetail(null)}
        canResolve={!isWarehouse && canVerify}
        onResolve={(row) => setReviewModal({ id: row.id })}
      />
      <SettlementDetailModal
        data={settlementDetail}
        onClose={() => setSettlementDetail(null)}
        canVerify={canVerify}
        busy={busy}
        onVerify={(id) => verifySettlement(id)}
        onReject={(row) => setRejectModal({ id: row.id })}
      />
      <SettlementFormModal
        open={!!settlementModal}
        initialDriver={settlementModal?.driver || null}
        drivers={drivers}
        busy={busy}
        onClose={() => setSettlementModal(null)}
        onSubmit={createSettlement}
      />
      <RejectModal
        open={!!rejectModal}
        busy={busy}
        onClose={() => setRejectModal(null)}
        onSubmit={(reason) => rejectSettlement({ id: rejectModal.id, reason })}
      />
      <ReviewModal
        open={!!reviewModal}
        busy={busy}
        onClose={() => setReviewModal(null)}
        onSubmit={(form) => resolveReview({ id: reviewModal.id, ...form })}
      />
    </div>
  );
}

function Overview({
  loading,
  summary,
  drivers,
  metrics,
  onOpenDriver,
  canSettle,
  isWarehouse,
  goCollections,
}) {
  const {
    totalCollected,
    directReceived,
    driverHeld,
    settled,
    outstanding,
    pendingVerification,
  } = metrics;
  const driverShare =
    n(totalCollected) > 0
      ? Math.min(100, (n(driverHeld) / n(totalCollected)) * 100)
      : 0;
  const directShare =
    n(totalCollected) > 0
      ? Math.min(100, (n(directReceived) / n(totalCollected)) * 100)
      : 0;
  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[1.15fr_.85fr]">
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
                Money path
              </p>
              <h2 className="mt-1 text-xl font-black text-slate-900">
                Where customer money is sitting
              </h2>
            </div>
            <Pill tone="pink">Live ledger</Pill>
          </div>
          <div className="mt-6 space-y-5">
            <FlowBar
              icon={CreditCard}
              label="Company Razorpay"
              value={directReceived}
              total={totalCollected}
              tone="blue"
              note={`${directShare.toFixed(1)}% of customer collections`}
            />
            <FlowBar
              icon={UsersRound}
              label="Collected by drivers"
              value={driverHeld}
              total={totalCollected}
              tone="pink"
              note={`${driverShare.toFixed(1)}% held/handled by drivers`}
            />
            <FlowBar
              icon={BadgeCheck}
              label="Driver money verified to company"
              value={settled}
              total={driverHeld || 1}
              tone="green"
              note={`${shortMoney(outstanding)} still outstanding`}
            />
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <MiniAmount
              label="Driver cash"
              value={pick(
                summary,
                "driver_cash_collected",
                "driverCashCollected",
              )}
              icon={Banknote}
            />
            <MiniAmount
              label="Driver UPI"
              value={pick(
                summary,
                "driver_upi_collected",
                "driverUpiCollected",
              )}
              icon={Smartphone}
            />
            <MiniAmount
              label="Pending verification"
              value={pendingVerification}
              icon={Clock3}
            />
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 p-5">
            <div>
              <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
                Drivers
              </p>
              <h2 className="mt-1 text-xl font-black text-slate-900">
                Highest outstanding
              </h2>
            </div>
            <button
              onClick={goCollections}
              className="text-xs font-black text-[#d86a78]"
            >
              View collections
            </button>
          </div>
          {loading ? (
            <div className="grid min-h-64 place-items-center text-slate-400">
              <Loader2 className="animate-spin" />
            </div>
          ) : drivers.length ? (
            <div className="divide-y divide-slate-100">
              {drivers.slice(0, 6).map((row, index) => (
                <div
                  key={pick(row, "driver_id", "driverId") || index}
                  className="flex items-center gap-3 p-4"
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[#fff1f3] font-black text-[#d86a78]">
                    {String(driverTitle(row)).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <b className="block truncate text-sm text-slate-800">
                      {driverTitle(row)}
                    </b>
                    <span className="mt-1 block text-xs text-slate-400">
                      {pick(row, "collection_count", "collectionCount") || 0}{" "}
                      collections ·{" "}
                      {shortMoney(pick(row, "settled_amount", "settledAmount"))}{" "}
                      settled
                    </span>
                  </div>
                  <div className="text-right">
                    <b className="block text-sm text-amber-600">
                      {shortMoney(
                        pick(row, "outstanding_amount", "outstandingAmount"),
                      )}
                    </b>
                    {canSettle ? (
                      <button
                        onClick={() => onOpenDriver(row)}
                        className="mt-1 text-[11px] font-black text-[#d86a78]"
                      >
                        Record settlement
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty text="No driver-held POD collections in this period." />
          )}
        </Card>
      </div>

      {!isWarehouse ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
                <PackageCheck size={20} />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400">
                  Grocery collections
                </p>
                <b className="text-xl font-black text-slate-900">
                  {pick(summary, "grocery_collections") || 0}
                </b>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-amber-50 text-amber-600">
                <Store size={20} />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400">
                  Food collections
                </p>
                <b className="text-xl font-black text-slate-900">
                  {pick(summary, "food_collections") || 0}
                </b>
              </div>
            </div>
          </Card>
        </div>
      ) : null}
    </>
  );
}
function FlowBar({ icon: Icon, label, value, total, tone, note }) {
  const percentage = Math.min(100, total > 0 ? (n(value) / n(total)) * 100 : 0);
  const colors = {
    blue: "bg-sky-500",
    pink: "bg-[#d86a78]",
    green: "bg-emerald-500",
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-extrabold text-slate-700">
          <Icon size={17} />
          {label}
        </div>
        <b className="text-sm text-slate-900">{money(value)}</b>
      </div>
      <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${colors[tone]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="mt-1 text-[11px] font-semibold text-slate-400">
        {note}
      </div>
    </div>
  );
}
function MiniAmount({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <Icon size={17} className="text-slate-400" />
      <span className="mt-3 block text-[11px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <b className="mt-1 block text-base font-black text-slate-800">
        {shortMoney(value)}
      </b>
    </div>
  );
}
function Empty({ text }) {
  return (
    <div className="grid min-h-48 place-items-center p-6 text-center text-sm font-semibold text-slate-400">
      {text}
    </div>
  );
}

function CollectionsTable({
  loading,
  data,
  onOpen,
  page,
  limit,
  setPage,
  setLimit,
}) {
  const rows = data.rows || [];
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
        <div>
          <h2 className="text-xl font-black text-slate-900">
            Customer collections
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Every POD V2 collection, settlement state and review flag.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
          <Filter size={14} />
          Server-side pagination
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px] text-left text-sm">
          <thead className="bg-slate-50/80 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-5 py-4">Order</th>
              <th className="px-4 py-4">Driver</th>
              <th className="px-4 py-4">Method</th>
              <th className="px-4 py-4">Breakdown</th>
              <th className="px-4 py-4">Settlement</th>
              <th className="px-4 py-4">Collected</th>
              <th className="px-4 py-4">State</th>
              <th className="px-5 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="8">
                  <div className="grid h-60 place-items-center text-slate-400">
                    <Loader2 className="animate-spin" />
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((row) => {
                const amount = collectionValue(row, "amountDue", "amount_due");
                const cash = collectionValue(row, "cashAmount", "cash_amount");
                const upi = collectionValue(
                  row,
                  "driverUpiAmount",
                  "driver_upi_amount",
                );
                const outstanding = collectionValue(
                  row,
                  "outstandingAmount",
                  "outstanding_amount",
                );
                const review = Boolean(
                  collectionValue(
                    row,
                    "duplicatePaymentReview",
                    "duplicate_payment_review",
                  ),
                );
                const status = collectionValue(
                  row,
                  "collectionStatus",
                  "collection_status",
                );
                return (
                  <tr key={row.id} className="transition hover:bg-slate-50/60">
                    <td className="px-5 py-4">
                      <b className="block text-slate-800">
                        {collectionValue(row, "orderNumber", "order_number") ||
                          String(
                            collectionValue(row, "orderId", "order_id") || "",
                          ).slice(0, 10)}
                      </b>
                      <span className="mt-1 block text-[11px] font-bold text-slate-400">
                        {labelize(
                          collectionValue(row, "orderType", "order_type"),
                        )}{" "}
                        · {money(amount)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <b className="block text-slate-700">{driverTitle(row)}</b>
                      <span className="text-[11px] text-slate-400">
                        {readDriver(row).phone ||
                          String(readDriver(row).id || "").slice(0, 12)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <MethodPill
                        mode={collectionValue(
                          row,
                          "selectedMode",
                          "selected_mode",
                        )}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between gap-4">
                          <span className="text-slate-400">Cash</span>
                          <b>{money(cash)}</b>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span className="text-slate-400">Driver UPI</span>
                          <b>{money(upi)}</b>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <b
                        className={
                          n(outstanding) > 0
                            ? "text-amber-600"
                            : "text-emerald-600"
                        }
                      >
                        {money(outstanding)}
                      </b>
                      <span className="mt-1 block text-[11px] text-slate-400">
                        outstanding
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs font-semibold text-slate-500">
                      {dateTime(
                        collectionValue(row, "collectedAt", "collected_at") ||
                          collectionValue(row, "createdAt", "created_at"),
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col items-start gap-1.5">
                        <Pill tone={statusTone(status)}>
                          {labelize(status)}
                        </Pill>
                        {review ? (
                          <Pill tone="red">Review required</Pill>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => onOpen(row)}
                        className="rounded-xl border border-[#f2c6cc] bg-[#fff8f9] px-3 py-2 text-xs font-black text-[#c65d6c] hover:bg-white"
                      >
                        View ledger
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8">
                  <Empty text="No collections match these filters." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={data.totalPages}
        total={data.total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />
    </Card>
  );
}

function financialStatusTone(financial) {
  if (!financial) return "slate";
  if (financial.financialHold) return "red";
  if (financial.policy?.enabled === false) return "slate";
  return "green";
}

function FinancialControlsPanel({ data, loading, q, setQ, page, limit, setPage, setLimit, onOpen }) {
  const rows = data?.rows || [];
  const hasFinancialReason = (financial, code) =>
    financial?.reasonCode === code ||
    (Array.isArray(financial?.reasons) && financial.reasons.some((item) => item?.code === code));
  const holds = rows.filter((row) => row?.financial?.financialHold).length;
  const overdue = rows.filter((row) => hasFinancialReason(row?.financial, "DRIVER_COLLECTION_SETTLEMENT_OVERDUE")).length;
  const overLimit = rows.filter((row) => hasFinancialReason(row?.financial, "DRIVER_COLLECTION_LIMIT_EXCEEDED")).length;
  const outstanding = rows.reduce((sum, row) => sum + n(row?.financial?.balance?.effectiveOutstandingAmount), 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={UsersRound} label="Drivers" value={String(data?.total || 0)} note="All driver accounts" tone="blue" />
        <StatCard icon={ShieldAlert} label="Financial hold" value={String(holds)} note="Visible on this page" tone={holds ? "amber" : "green"} />
        <StatCard icon={Clock3} label="Overdue" value={String(overdue)} note="Settlement deadline crossed" tone={overdue ? "amber" : "slate"} />
        <StatCard icon={WalletCards} label="Page outstanding" value={shortMoney(outstanding)} note={`${overLimit} driver(s) above amount limit`} tone={overLimit ? "amber" : "slate"} />
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-slate-100 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1 text-[11px] font-black uppercase tracking-[.14em] text-[#c65d6c]">
              <UserCog size={14} /> Risk & eligibility
            </div>
            <h2 className="mt-2 text-xl font-black text-slate-900">Driver collection limits</h2>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-slate-500">
              A driver is blocked from new Grocery + Food orders when the amount limit or settlement deadline is crossed. Current delivery remains completable.
            </p>
          </div>
          <div className="relative w-full lg:w-[340px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search driver, phone, vehicle..."
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-xs font-semibold outline-none focus:border-[#d86a78]"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] text-left text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-5 py-4">Driver</th>
                <th className="px-4 py-4">Standing</th>
                <th className="px-4 py-4">Outstanding</th>
                <th className="px-4 py-4">Pending verify</th>
                <th className="px-4 py-4">Settlement due</th>
                <th className="px-4 py-4">Effective policy</th>
                <th className="px-5 py-4 text-right">Control</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="7"><div className="grid h-64 place-items-center"><Loader2 className="animate-spin text-slate-400" /></div></td></tr>
              ) : rows.length ? rows.map((row) => {
                const f = row?.financial || {};
                const balance = f.balance || {};
                const policy = f.policy || {};
                return (
                  <tr key={row.id} className="hover:bg-slate-50/60">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#fff1f3] font-black text-[#d86a78]">{String(row.name || row.partnerId || "D").charAt(0).toUpperCase()}</div>
                        <div className="min-w-0">
                          <b className="block max-w-[210px] truncate text-slate-800">{row.name || row.partnerId || `Driver ${String(row.id || "").slice(0, 8)}`}</b>
                          <span className="block text-[11px] text-slate-400">{row.phone || row.vehicleNumber || row.partnerId || "—"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Pill tone={financialStatusTone(f)}>{f.financialHold ? "Financial hold" : policy.enabled === false ? "Controls off" : "Eligible"}</Pill>
                      <span className={`mt-1 block max-w-[220px] text-[11px] leading-4 ${f.financialHold ? "text-rose-500" : "text-slate-400"}`}>{f.message || "—"}</span>
                    </td>
                    <td className="px-4 py-4">
                      <b className={f.financialHold ? "text-rose-600" : "text-slate-900"}>{money(balance.effectiveOutstandingAmount)}</b>
                      <span className="mt-1 block text-[11px] text-slate-400">Raw {money(balance.rawOutstandingAmount)}</span>
                    </td>
                    <td className="px-4 py-4 font-bold text-sky-600">{money(balance.pendingVerificationAmount)}</td>
                    <td className="px-4 py-4 text-xs">
                      <b className={balance.oldestDueAt && new Date(balance.oldestDueAt).getTime() < Date.now() && !balance.graceActive ? "text-rose-600" : "text-slate-700"}>{dateTime(balance.oldestDueAt)}</b>
                      {policy.graceUntil ? <span className="mt-1 block text-[11px] text-emerald-600">Grace until {dateTime(policy.graceUntil)}</span> : null}
                    </td>
                    <td className="px-4 py-4 text-xs">
                      <b className="block text-slate-700">{money(policy.maxOutstandingAmount)} max</b>
                      <span className="mt-1 block text-slate-400">{policy.settlementDays || "—"} day settlement · {policy.source === "DRIVER_OVERRIDE" ? "Custom" : "Global"}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => onOpen(row)} className="rounded-xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-slate-800">Manage</button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="7"><Empty text="No drivers match this search." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={data?.totalPages || 1} total={data?.total || 0} limit={limit} onPage={setPage} onLimit={setLimit} />
      </Card>
    </div>
  );
}

function FinancialDriverModal({ data, loading, busy, canEdit, onClose, onSave, onExtend, onClear }) {
  const driver = data?.driver || {};
  const financial = data?.financial || {};
  const policy = financial?.policy || {};
  const override = policy?.override || null;
  const balance = financial?.balance || {};
  const events = Array.isArray(data?.events) ? data.events : [];
  const openCollections = Array.isArray(financial?.openCollections) ? financial.openCollections : [];
  const [customDays, setCustomDays] = useState(false);
  const [customLimit, setCustomLimit] = useState(false);
  const [settlementDays, setSettlementDays] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [reason, setReason] = useState("");
  const [graceUntil, setGraceUntil] = useState("");
  const [extendReason, setExtendReason] = useState("");
  const [clearReason, setClearReason] = useState("");

  useEffect(() => {
    if (!data?.driver?.id) return;
    setCustomDays(override?.settlementDays != null);
    setCustomLimit(override?.maxOutstandingAmount != null);
    setSettlementDays(String(override?.settlementDays ?? policy?.settlementDays ?? 2));
    setMaxAmount(String(override?.maxOutstandingAmount ?? policy?.maxOutstandingAmount ?? 10000));
    setReason(override?.reason || "");
    setGraceUntil(toLocalDateTimeInput(policy?.graceUntil));
    setExtendReason("");
    setClearReason("");
  }, [data?.driver?.id, override?.settlementDays, override?.maxOutstandingAmount, override?.reason, policy?.settlementDays, policy?.maxOutstandingAmount, policy?.graceUntil]);

  if (!data) return null;
  return (
    <Modal open title="Driver financial control" subtitle="Change only this driver's collection risk policy without affecting other drivers." onClose={onClose} wide>
      {loading ? <div className="grid h-72 place-items-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-5">
          <div className={`rounded-[22px] border p-5 ${financial.financialHold ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2"><Pill tone={financial.financialHold ? "red" : "green"}>{financial.financialHold ? "Financial hold" : "Eligible for new orders"}</Pill><Pill>{policy.source === "DRIVER_OVERRIDE" ? "Custom policy" : "Global policy"}</Pill></div>
                <h3 className="mt-3 text-2xl font-black text-slate-900">{driver.name || driver.partnerId || "Driver"}</h3>
                <p className={`mt-1 text-sm font-semibold ${financial.financialHold ? "text-rose-700" : "text-emerald-700"}`}>{financial.message || "—"}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-right">
                <div className="rounded-2xl bg-white/75 px-4 py-3"><span className="block text-[10px] font-black uppercase text-slate-400">Outstanding</span><b className="text-lg text-slate-900">{money(balance.effectiveOutstandingAmount)}</b></div>
                <div className="rounded-2xl bg-white/75 px-4 py-3"><span className="block text-[10px] font-black uppercase text-slate-400">Pending verify</span><b className="text-lg text-sky-600">{money(balance.pendingVerificationAmount)}</b></div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_.9fr]">
            <Card className="p-5">
              <div className="flex items-center justify-between gap-3"><div><p className="text-xs font-black uppercase tracking-[.14em] text-slate-400">Custom override</p><h3 className="mt-1 text-lg font-black text-slate-900">Limit & settlement window</h3></div><Gauge className="text-[#d86a78]" /></div>
              <div className="mt-5 space-y-4">
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input type="checkbox" checked={customLimit} disabled={!canEdit} onChange={(e) => setCustomLimit(e.target.checked)} />
                  <span className="flex-1"><b className="block text-sm text-slate-800">Custom maximum outstanding</b><small className="text-slate-400">Global: {money(policy?.defaults?.defaultMaxOutstandingAmount ?? policy.maxOutstandingAmount)}</small></span>
                  <input type="number" min="0" disabled={!canEdit || !customLimit} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} className="w-32 rounded-xl border border-slate-200 px-3 py-2 text-right text-sm font-black" />
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input type="checkbox" checked={customDays} disabled={!canEdit} onChange={(e) => setCustomDays(e.target.checked)} />
                  <span className="flex-1"><b className="block text-sm text-slate-800">Custom settlement days</b><small className="text-slate-400">Global: {policy?.defaults?.defaultSettlementDays ?? policy.settlementDays} days</small></span>
                  <input type="number" min="1" max="90" disabled={!canEdit || !customDays} value={settlementDays} onChange={(e) => setSettlementDays(e.target.value)} className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-right text-sm font-black" />
                </label>
                <textarea disabled={!canEdit} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for driver-specific change" className="min-h-24 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-[#d86a78]" />
                {canEdit ? <button disabled={busy} onClick={() => onSave(driver.id, { settlementDays: customDays ? Number(settlementDays) : null, maxOutstandingAmount: customLimit ? Number(maxAmount) : null, reason: reason || null })} className="inline-flex items-center gap-2 rounded-xl bg-[#d86a78] px-4 py-2.5 text-xs font-black text-white disabled:opacity-60"><Save size={15} />Save driver override</button> : null}
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 text-amber-600"><TimerReset size={18} /></div><div><p className="text-xs font-black uppercase tracking-[.14em] text-slate-400">Deadline extension</p><h3 className="text-lg font-black text-slate-900">Temporary grace</h3></div></div>
              <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-600"><b>Oldest due:</b> {dateTime(balance.oldestDueAt)}<br/><b>Current grace:</b> {dateTime(policy.graceUntil)}</div>
              <div className="mt-4 space-y-3">
                <input type="datetime-local" disabled={!canEdit} value={graceUntil} onChange={(e) => setGraceUntil(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold" />
                <textarea disabled={!canEdit} value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Why is this deadline being extended?" className="min-h-20 w-full rounded-xl border border-slate-200 p-3 text-sm" />
                {canEdit ? <button disabled={busy || !fromLocalDateTimeInput(graceUntil)} onClick={() => onExtend(driver.id, { graceUntil: fromLocalDateTimeInput(graceUntil), reason: extendReason || null })} className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white disabled:opacity-40"><TimerReset size={15} />Extend deadline</button> : null}
              </div>
            </Card>
          </div>

          {override && canEdit ? (
            <Card className="border-rose-100 bg-rose-50/50 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div><b className="text-sm text-slate-900">Return to global policy</b><p className="mt-1 text-xs text-slate-500">Removes custom amount/day/grace settings for this driver.</p></div>
                <div className="flex flex-1 gap-2 md:max-w-xl"><input value={clearReason} onChange={(e) => setClearReason(e.target.value)} placeholder="Reason for clearing override" className="min-w-0 flex-1 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs"/><button disabled={busy} onClick={() => onClear(driver.id, clearReason)} className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2 text-xs font-black text-rose-600"><RotateCcw size={14}/>Use global</button></div>
              </div>
            </Card>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-[1fr_.9fr]">
            <Card className="overflow-hidden">
              <div className="border-b border-slate-100 p-5"><h3 className="font-black text-slate-900">Open driver-held collections</h3><p className="mt-1 text-xs text-slate-400">These liabilities determine outstanding and the oldest due date.</p></div>
              <div className="max-h-[360px] overflow-auto divide-y divide-slate-100">
                {openCollections.length ? openCollections.map((row) => <div key={row.id} className="flex items-center justify-between gap-4 p-4"><div><b className="text-sm text-slate-800">{row.order_number || row.orderNumber || "Order"}</b><span className="mt-1 block text-[11px] text-slate-400">Collected {dateTime(row.collected_at || row.collectedAt)} · Due {dateTime(row.settlement_due_at || row.settlementDueAt)}</span></div><b className="text-sm text-amber-600">{money(row.outstanding_amount ?? row.outstandingAmount)}</b></div>) : <Empty text="No open driver-held collections." />}
              </div>
            </Card>
            <Card className="overflow-hidden">
              <div className="border-b border-slate-100 p-5"><h3 className="font-black text-slate-900">Policy audit trail</h3><p className="mt-1 text-xs text-slate-400">Driver-specific changes and deadline extensions.</p></div>
              <div className="max-h-[360px] overflow-auto divide-y divide-slate-100">
                {events.length ? events.map((event, index) => <div key={event.id || index} className="p-4"><div className="flex items-start justify-between gap-3"><b className="text-xs text-slate-700">{labelize(event.event_type || event.eventType)}</b><span className="text-[10px] text-slate-400">{dateTime(event.created_at || event.createdAt)}</span></div>{event.reason ? <p className="mt-1 text-xs leading-5 text-slate-500">{event.reason}</p> : null}</div>) : <Empty text="No driver-specific financial changes yet." />}
              </div>
            </Card>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DriversTable({ rows, loading, canSettle, isWarehouse = false, onSettle }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-100 p-5">
        <h2 className="text-xl font-black text-slate-900">
          Driver liabilities
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Cash + driver UPI collected from customers versus verified
          settlements.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-5 py-4">Driver</th>
              <th className="px-4 py-4">Customer collections</th>
              <th className="px-4 py-4">Cash / UPI</th>
              <th className="px-4 py-4">Settled</th>
              <th className="px-4 py-4">Pending verification</th>
              <th className="px-4 py-4">Outstanding</th>
              {isWarehouse ? <th className="px-4 py-4">Order eligibility</th> : null}
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={isWarehouse ? 8 : 7}>
                  <div className="grid h-56 place-items-center">
                    <Loader2 className="animate-spin text-slate-400" />
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((row, index) => (
                <tr
                  key={readDriver(row).id || index}
                  className="hover:bg-slate-50/60"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#fff1f3] font-black text-[#d86a78]">
                        {driverTitle(row).charAt(0)}
                      </div>
                      <div>
                        <b className="text-slate-800">{driverTitle(row)}</b>
                        <span className="block text-[11px] text-slate-400">
                          {pick(row, "collection_count", "collectionCount") ||
                            0}{" "}
                          orders
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 font-black text-slate-800">
                    {money(pick(row, "total_collected", "totalCollected"))}
                  </td>
                  <td className="px-4 py-4 text-xs">
                    <span className="block">
                      Cash {money(pick(row, "cash_collected", "cashCollected"))}
                    </span>
                    <span className="mt-1 block text-slate-500">
                      UPI {money(pick(row, "upi_collected", "upiCollected"))}
                    </span>
                  </td>
                  <td className="px-4 py-4 font-bold text-emerald-600">
                    {money(pick(row, "settled_amount", "settledAmount"))}
                  </td>
                  <td className="px-4 py-4 font-bold text-sky-600">
                    {money(
                      pick(
                        row,
                        "pending_verification_amount",
                        "pendingVerificationAmount",
                      ),
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <b className="text-amber-600">
                      {money(
                        pick(row, "outstanding_amount", "outstandingAmount"),
                      )}
                    </b>
                    <span className="block text-[11px] text-slate-400">
                      Available{" "}
                      {money(
                        pick(
                          row,
                          "available_to_settle_amount",
                          "availableToSettleAmount",
                        ),
                      )}
                    </span>
                  </td>
                  {isWarehouse ? (
                    <td className="px-4 py-4">
                      {row?.financial?.financialHold ? (
                        <div className="max-w-[220px]">
                          <Pill tone="red">Financial hold</Pill>
                          <span className="mt-1 block text-[11px] leading-4 text-rose-500">
                            {row.financial.message || "Submit company money before new orders."}
                          </span>
                        </div>
                      ) : row?.financial ? (
                        <div>
                          <Pill tone="green">Eligible</Pill>
                          <span className="mt-1 block text-[11px] text-slate-400">
                            Limit {money(row.financial?.policy?.maxOutstandingAmount || 0)} · {row.financial?.policy?.settlementDays || "—"} days
                          </span>
                        </div>
                      ) : (
                        <Pill tone="slate">Status unavailable</Pill>
                      )}
                    </td>
                  ) : null}
                  <td className="px-5 py-4 text-right">
                    {canSettle &&
                    n(
                      pick(
                        row,
                        "available_to_settle_amount",
                        "availableToSettleAmount",
                      ),
                    ) > 0 ? (
                      <button
                        onClick={() => onSettle(row)}
                        className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white"
                      >
                        Settle
                      </button>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isWarehouse ? 8 : 7}>
                  <Empty text="No driver liabilities found." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SettlementsTable({
  data,
  loading,
  onOpen,
  onCreate,
  canSettle,
  page,
  limit,
  setPage,
  setLimit,
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
        <div>
          <h2 className="text-xl font-black text-slate-900">
            Settlement ledger
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Money deposited/transferred by drivers back to the company.
          </p>
        </div>
        {canSettle ? (
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white"
          >
            <ArrowDownToLine size={15} />
            Record settlement
          </button>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-5 py-4">Driver</th>
              <th className="px-4 py-4">Amount</th>
              <th className="px-4 py-4">Method</th>
              <th className="px-4 py-4">Reference</th>
              <th className="px-4 py-4">Status</th>
              <th className="px-4 py-4">Created</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="7">
                  <div className="grid h-56 place-items-center">
                    <Loader2 className="animate-spin text-slate-400" />
                  </div>
                </td>
              </tr>
            ) : data.rows?.length ? (
              data.rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/60">
                  <td className="px-5 py-4">
                    <b>{driverTitle(row)}</b>
                    <span className="block text-[11px] text-slate-400">
                      {String(pick(row, "driver_id", "driverId") || "").slice(
                        0,
                        14,
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-4 font-black text-slate-900">
                    {money(row.amount)}
                  </td>
                  <td className="px-4 py-4">
                    <Pill tone="blue">
                      {labelize(
                        pick(row, "settlement_method", "settlementMethod"),
                      )}
                    </Pill>
                  </td>
                  <td className="px-4 py-4 text-xs font-semibold text-slate-500">
                    {row.reference || "—"}
                  </td>
                  <td className="px-4 py-4">
                    <Pill tone={statusTone(row.status)}>
                      {labelize(row.status)}
                    </Pill>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500">
                    {dateTime(pick(row, "created_at", "createdAt"))}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => onOpen(row)}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-50"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7">
                  <Empty text="No settlement records found." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={data.totalPages}
        total={data.total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />
    </Card>
  );
}

function SetupPanel({ scope, settings, loading, busy, canEdit, warehouseOptions = [], restaurantOptions = [], onSave }) {
  const isWarehouse = scope === "warehouse";
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (settings) setDraft(JSON.parse(JSON.stringify(settings)));
  }, [settings]);
  if (loading || !draft)
    return (
      <Card>
        <div className="grid h-72 place-items-center text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      </Card>
    );

  if (isWarehouse) {
    const global = draft.global || {};
    const override = draft.override || {};
    const effective = draft.effective || {};
    const toggle = (key, value) =>
      setDraft((d) => ({
        ...d,
        override: { ...(d.override || {}), [key]: value },
      }));
    return (
      <div className="grid gap-4 xl:grid-cols-[1fr_.72fr]">
        <Card className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
                Warehouse setup
              </p>
              <h2 className="mt-1 text-2xl font-black text-slate-900">
                Allowed doorstep methods
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                A warehouse can only restrict methods allowed by admin. It
                cannot turn on a method disabled globally.
              </p>
            </div>
            <Settings2 className="text-[#d86a78]" />
          </div>
          <div className="mt-6 grid gap-3">
            <Switch
              checked={
                (override.companyOnlineEnabled ?? true) &&
                global.companyOnlineEnabled !== false
              }
              disabled={!canEdit || global.companyOnlineEnabled === false}
              onChange={(v) => toggle("companyOnlineEnabled", v)}
              label="Full online — Company Razorpay"
              description="Customer pays the complete POD amount to company Razorpay."
            />
            <Switch
              checked={
                (override.driverCashEnabled ?? true) &&
                global.driverCashEnabled !== false
              }
              disabled={!canEdit || global.driverCashEnabled === false}
              onChange={(v) => toggle("driverCashEnabled", v)}
              label="Driver Cash"
              description="Driver confirms physical cash and the amount becomes driver liability."
            />
            <Switch
              checked={
                (override.driverSplitEnabled ?? true) &&
                global.driverSplitEnabled !== false
              }
              disabled={!canEdit || global.driverSplitEnabled === false}
              onChange={(v) => toggle("driverSplitEnabled", v)}
              label="Split — Cash + Driver UPI"
              description="Split never mixes company Razorpay. Complete amount remains driver liability."
            />
          </div>
          {canEdit ? (
            <button
              disabled={busy}
              onClick={() =>
                onSave({
                  companyOnlineEnabled:
                    draft.override?.companyOnlineEnabled ?? true,
                  driverCashEnabled: draft.override?.driverCashEnabled ?? true,
                  driverSplitEnabled:
                    draft.override?.driverSplitEnabled ?? true,
                })
              }
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#d86a78] px-5 py-3 text-sm font-black text-white disabled:opacity-60"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
              Save warehouse setup
            </button>
          ) : null}
        </Card>
        <Card className="p-6">
          <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
            Effective state
          </p>
          <h3 className="mt-1 text-xl font-black text-slate-900">
            What new POD orders can use
          </h3>
          <div className="mt-5 space-y-3">
            <EffectiveRow
              label="POD enabled"
              value={draft.payOnDeliveryEnabled}
            />
            <EffectiveRow
              label="Hybrid V2 rollout"
              value={draft.collectionFlowEnabled}
            />
            <EffectiveRow
              label="Company online"
              value={effective.companyOnlineEnabled}
            />
            <EffectiveRow
              label="Driver cash"
              value={effective.driverCashEnabled}
            />
            <EffectiveRow
              label="Driver split"
              value={effective.driverSplitEnabled}
            />
            <EffectiveRow
              label="Customer delivery OTP"
              value={draft.deliveryOtp?.effective?.required}
            />
            {draft.deliveryOtp?.effective?.disabledUntil ? (
              <div className="rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700">
                OTP temporarily disabled until {dateTime(draft.deliveryOtp.effective.disabledUntil)}
              </div>
            ) : null}
          </div>
          <div className="mt-5 rounded-2xl bg-amber-50 p-4 text-xs font-semibold leading-5 text-amber-800">
            Global admin settings always win. Pausing V2 affects only new
            orders; already-created V2 orders remain collectable.
          </div>
        </Card>
      </div>
    );
  }

  const flow = draft.collectionFlow || {};
  const financialPolicy = flow.driverFinancialPolicy || {};
  const deliveryOtp = draft.deliveryOtp || { enabled: true, disabledUntil: null, warehouseOverrides: [], restaurantOverrides: [] };
  const setRoot = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setFlow = (key, value) =>
    setDraft((d) => ({
      ...d,
      collectionFlow: { ...(d.collectionFlow || {}), [key]: value },
    }));
  const setFinancialPolicy = (key, value) =>
    setDraft((d) => ({
      ...d,
      collectionFlow: {
        ...(d.collectionFlow || {}),
        driverFinancialPolicy: {
          ...(d.collectionFlow?.driverFinancialPolicy || {}),
          [key]: value,
        },
      },
    }));
  const setDeliveryOtp = (next) =>
    setDraft((d) => ({
      ...d,
      deliveryOtp: typeof next === "function" ? next(d.deliveryOtp || {}) : next,
    }));
  return (
    <div className="grid gap-4 xl:grid-cols-[1.1fr_.9fr]">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
              Global master controls
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h2 className="text-2xl font-black text-slate-900">
                Payment Collection V2 setup
              </h2>
              <InfoTip text="These controls apply to Grocery + Food. Warehouse or restaurant overrides can only restrict a method that is already allowed globally." />
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Global rules for new POD orders.
            </p>
          </div>
          <ShieldCheck className="text-[#d86a78]" />
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          <Switch
            checked={Boolean(draft.payOnDeliveryEnabled)}
            disabled={!canEdit}
            onChange={(v) => setRoot("payOnDeliveryEnabled", v)}
            label="Pay on Delivery"
            description="Master availability"
            info="Turn this on to allow Pay on Delivery on supported orders. If this is off, no POD collection method can be used."
          />
          <Switch
            checked={Boolean(flow.enabled)}
            disabled={!canEdit}
            onChange={(v) => setFlow("enabled", v)}
            label="Hybrid V2 rollout"
            description="Controls new-order rollout"
            info="When ON, newly-created POD orders use the V2 payment collection flow. Existing V1 POD orders stay on the old flow and are never auto-converted."
          />
        </div>
        <div className="my-6 h-px bg-slate-100" />
        <div className="grid gap-3">
          <Switch
            checked={Boolean(flow.companyOnlineEnabled)}
            disabled={!canEdit}
            onChange={(v) => setFlow("companyOnlineEnabled", v)}
            label="Full online — Company Razorpay"
            description="Company receives full payment"
            info="Customer pays the complete POD amount online at the doorstep. The amount goes directly to the company Razorpay account."
          />
          <Switch
            checked={Boolean(flow.driverCashEnabled)}
            disabled={!canEdit}
            onChange={(v) => setFlow("driverCashEnabled", v)}
            label="Driver Cash"
            description="Driver handles cash"
            info="Use this when the driver collects full cash from the customer. The collected amount becomes driver liability until settlement is verified."
          />
          <Switch
            checked={Boolean(flow.driverSplitEnabled)}
            disabled={!canEdit}
            onChange={(v) => setFlow("driverSplitEnabled", v)}
            label="Split — Driver Cash + Driver UPI"
            description="Cash + driver UPI"
            info="Use this when the amount is split between driver-collected cash and driver UPI. Company Razorpay is never mixed into this method."
          />
        </div>
        <div className="my-6 h-px bg-slate-100" />
        <div className="grid gap-3 md:grid-cols-2">
          <Switch
            checked={Boolean(flow.requireDriverUpiReference)}
            disabled={!canEdit}
            onChange={(v) => setFlow("requireDriverUpiReference", v)}
            label="Require UPI reference"
            description="Keep proof for split UPI"
            info="Requires the driver to enter a transaction or UTR reference whenever split payment includes a driver UPI transfer."
          />
          <Switch
            checked={Boolean(flow.requireDriverUpiIdForSplit)}
            disabled={!canEdit}
            onChange={(v) => setFlow("requireDriverUpiIdForSplit", v)}
            label="Require driver UPI account"
            description="Driver UPI must exist"
            info="Blocks split payment unless the assigned driver already has an active UPI ID saved in the system."
          />
          <Switch
            checked={Boolean(flow.allowMethodSwitchBeforePayment)}
            disabled={!canEdit}
            onChange={(v) => setFlow("allowMethodSwitchBeforePayment", v)}
            label="Allow safe method switch"
            description="Allow verified switch"
            info="Lets the team close an unpaid Razorpay QR and switch the collection method only after provider verification confirms that no payment was captured."
          />
          <Switch
            checked={Boolean(flow.settlementVerificationRequired)}
            disabled={!canEdit}
            onChange={(v) => setFlow("settlementVerificationRequired", v)}
            label="Settlement verification"
            description="Manual approval needed"
            info="Keeps settlements in pending state until an authorized admin or warehouse user verifies them."
          />
        </div>

        <div className="my-6 h-px bg-slate-100" />
        <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(135deg,#fff_0%,#fff8f9_100%)] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1 text-[10px] font-black uppercase tracking-[.14em] text-[#c65d6c]"><ShieldAlert size={13}/> Driver risk policy</div>
              <div className="mt-2 flex items-center gap-2">
                <h3 className="text-lg font-black text-slate-900">Collected-money guardrail</h3>
                <InfoTip text="This policy helps reduce risk by stopping new order assignment when a driver crosses the allowed outstanding amount or misses the settlement deadline. Ongoing delivery is not interrupted." />
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-500">Protects assignment after amount or deadline risk.</p>
            </div>
            <Pill tone={financialPolicy.enabled ? "green" : "slate"}>{financialPolicy.enabled ? "Enforced" : "Safe rollout OFF"}</Pill>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Switch checked={Boolean(financialPolicy.enabled)} disabled={!canEdit} onChange={(v) => setFinancialPolicy("enabled", v)} label="Enable financial hold" description="Turn guardrail on" info="When enabled, amount-limit and deadline rules are enforced during driver availability and order assignment." />
            <Switch checked={financialPolicy.blockOnAmountLimit !== false} disabled={!canEdit} onChange={(v) => setFinancialPolicy("blockOnAmountLimit", v)} label="Block on amount limit" description="Stops on amount limit" info="Prevents new order assignment once the driver's effective outstanding amount crosses the allowed maximum." />
            <Switch checked={financialPolicy.blockOnOverdue !== false} disabled={!canEdit} onChange={(v) => setFinancialPolicy("blockOnOverdue", v)} label="Block on overdue" description="Stops on overdue settlement" info="Prevents new order assignment when the oldest driver-held liability becomes overdue." />
            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-white p-3">
              <label className="text-[11px] font-bold text-slate-500">Settlement days<input type="number" min="1" max="90" disabled={!canEdit} value={financialPolicy.defaultSettlementDays ?? 2} onChange={(e) => setFinancialPolicy("defaultSettlementDays", Number(e.target.value || 1))} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-800"/></label>
              <label className="text-[11px] font-bold text-slate-500">Max outstanding<input type="number" min="0" disabled={!canEdit} value={financialPolicy.defaultMaxOutstandingAmount ?? 10000} onChange={(e) => setFinancialPolicy("defaultMaxOutstandingAmount", Number(e.target.value || 0))} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-800"/></label>
            </div>
          </div>
        </div>
        {canEdit ? (
          <button
            disabled={busy}
            onClick={() =>
              onSave({
                payOnDeliveryEnabled: draft.payOnDeliveryEnabled,
                provider: draft.provider || "RAZORPAY",
                collectionFlow: draft.collectionFlow,
                deliveryOtp: draft.deliveryOtp,
                warehouseOverrides: draft.warehouseOverrides,
                restaurantOverrides: draft.restaurantOverrides,
              })
            }
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#d86a78] px-5 py-3 text-sm font-black text-white shadow-[0_10px_25px_rgba(216,106,120,.25)] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
            Save global setup
          </button>
        ) : null}
      </Card>
      <div className="space-y-4">
        <Card className="p-6">
          <div className="flex items-center gap-2">
            <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
              Production guardrail
            </p>
            <InfoTip text="Use this panel to quickly verify the currently active rollout state and how many local overrides exist in production." />
          </div>
          <div className="mt-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle
              className="mt-0.5 shrink-0 text-amber-600"
              size={20}
            />
            <div>
              <b className="text-sm text-amber-900">
                Rollout switch: {flow.enabled ? "ON" : "OFF"}
              </b>
              <p className="mt-1 text-xs leading-5 text-amber-800">
                Use V2 only after all related apps are deployed.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <EffectiveRow
              label="Provider"
              text={draft.provider || "RAZORPAY"}
            />
            <EffectiveRow label="Flow version" text={`V${flow.version || 2}`} />
            <EffectiveRow
              label="Warehouse overrides"
              text={String(draft.warehouseOverrides?.length || 0)}
            />
            <EffectiveRow
              label="Restaurant overrides"
              text={String(draft.restaurantOverrides?.length || 0)}
            />
          </div>
        </Card>
        <DeliveryOtpSettings
          value={deliveryOtp}
          canEdit={canEdit}
          warehouseOptions={warehouseOptions}
          restaurantOptions={restaurantOptions}
          onChange={setDeliveryOtp}
        />
        <Card className="p-6">
          <div className="flex items-center gap-2">
            <p className="text-xs font-black uppercase tracking-[.15em] text-slate-400">
              Locked accounting model
            </p>
            <InfoTip text="These are the fixed accounting outcomes for each enabled collection method. This card is only for quick understanding and does not change any setting." />
          </div>
          <div className="mt-4 space-y-4">
            <SetupFlow
              icon={CreditCard}
              title="100% online"
              text="Company Razorpay → company received, driver liability ₹0."
            />
            <SetupFlow
              icon={Banknote}
              title="Cash"
              text="Driver collects → customer paid → driver owes company."
            />
            <SetupFlow
              icon={Split}
              title="Split"
              text="Cash + Driver UPI → complete amount is driver liability."
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
function DeliveryOtpSettings({ value, canEdit, warehouseOptions = [], restaurantOptions = [], onChange }) {
  const otp = {
    enabled: value?.enabled !== false,
    disabledUntil: value?.disabledUntil || null,
    warehouseOverrides: Array.isArray(value?.warehouseOverrides) ? value.warehouseOverrides : [],
    restaurantOverrides: Array.isArray(value?.restaurantOverrides) ? value.restaurantOverrides : [],
  };
  const [customUntil, setCustomUntil] = useState(toLocalDateTimeInput(otp.disabledUntil));

  useEffect(() => {
    setCustomUntil(toLocalDateTimeInput(value?.disabledUntil));
  }, [value?.disabledUntil]);

  const update = (patch) => onChange({ ...otp, ...patch });
  const activeTemporaryPause = Boolean(otp.disabledUntil && new Date(otp.disabledUntil).getTime() > Date.now());
  const globallyRequired = otp.enabled && !activeTemporaryPause;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-[10px] font-black uppercase tracking-[.14em] text-violet-600"><ShieldCheck size={13}/> Delivery verification</div>
          <div className="mt-2 flex items-center gap-2">
            <h3 className="text-xl font-black text-slate-900">Customer Delivery OTP</h3>
            <InfoTip text="Pickup OTP is not changed here. These settings only control whether a customer-facing delivery OTP step is required after payment collection." />
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">Controls the customer-side delivery verification step.</p>
        </div>
        <Pill tone={globallyRequired ? "green" : "amber"}>{globallyRequired ? "OTP required" : "OTP bypass active"}</Pill>
      </div>

      <div className="mt-5 grid gap-3">
        <Switch
          checked={otp.enabled}
          disabled={!canEdit}
          onChange={(enabled) => update({ enabled, disabledUntil: enabled ? otp.disabledUntil : null })}
          label="Delivery OTP master switch"
          description="Enable or bypass OTP"
          info="Turning this OFF disables customer Delivery OTP until an admin enables it again. POD payment collection still remains mandatory."
        />
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[.12em] text-amber-700"><Clock3 size={15}/> Peak-hour temporary bypass <InfoTip text="Use this for temporary OTP bypass during peak hours or operational issues. The bypass ends automatically at the selected time." /></div>
        <div className="mt-3 flex flex-wrap gap-2">
          {[30, 60, 120].map((minutes) => (
            <button key={minutes} disabled={!canEdit || !otp.enabled} onClick={() => update({ disabledUntil: futureIso(minutes) })} className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-[11px] font-black text-amber-700 disabled:opacity-40">{minutes < 60 ? `${minutes} min` : `${minutes / 60} hour${minutes > 60 ? "s" : ""}`}</button>
          ))}
          <button disabled={!canEdit || !otp.enabled} onClick={() => update({ disabledUntil: endOfTodayIso() })} className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-[11px] font-black text-amber-700 disabled:opacity-40">Until end of day</button>
          <button disabled={!canEdit} onClick={() => update({ enabled: true, disabledUntil: null })} className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-[11px] font-black text-emerald-700 disabled:opacity-40">Require OTP now</button>
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input type="datetime-local" disabled={!canEdit || !otp.enabled} value={customUntil} onChange={(e) => setCustomUntil(e.target.value)} className="min-w-0 flex-1 rounded-xl border border-amber-200 bg-white px-3 py-2.5 text-xs font-semibold" />
          <button disabled={!canEdit || !otp.enabled || !fromLocalDateTimeInput(customUntil)} onClick={() => update({ disabledUntil: fromLocalDateTimeInput(customUntil) })} className="rounded-xl bg-amber-600 px-4 py-2.5 text-xs font-black text-white disabled:opacity-40">Apply custom time</button>
        </div>
        <p className="mt-2 text-[11px] font-semibold text-amber-700">Current temporary bypass: {activeTemporaryPause ? `until ${dateTime(otp.disabledUntil)}` : "none"}</p>
      </div>

      <div className="mt-5 grid gap-4">
        <OtpOverrideManager
          type="warehouse"
          icon={Building2}
          title="Warehouse overrides"
          options={warehouseOptions}
          items={otp.warehouseOverrides}
          canEdit={canEdit}
          globalRequired={globallyRequired}
          onChange={(warehouseOverrides) => update({ warehouseOverrides })}
        />
        <OtpOverrideManager
          type="restaurant"
          icon={UtensilsCrossed}
          title="Food restaurant overrides"
          options={restaurantOptions}
          items={otp.restaurantOverrides}
          canEdit={canEdit}
          globalRequired={globallyRequired}
          onChange={(restaurantOverrides) => update({ restaurantOverrides })}
        />
      </div>
    </Card>
  );
}

function OtpOverrideManager({ type, icon: Icon, title, options, items, canEdit, globalRequired, onChange }) {
  const idKey = type === "warehouse" ? "warehouseId" : "restaurantId";
  const nameKey = type === "warehouse" ? "warehouseName" : "restaurantName";
  const [selectedId, setSelectedId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [until, setUntil] = useState("");
  const safeItems = Array.isArray(items) ? items : [];

  const save = () => {
    if (!selectedId) return;
    const option = options.find((item) => String(item.id) === String(selectedId));
    const next = {
      [idKey]: String(selectedId),
      [nameKey]: option?.name || null,
      enabled,
      disabledUntil: enabled ? null : (fromLocalDateTimeInput(until) || null),
    };
    const index = safeItems.findIndex((item) => String(item?.[idKey] || item?.[type === "warehouse" ? "warehouse_id" : "restaurant_id"] || "") === String(selectedId));
    const updated = safeItems.map((item) => ({ ...item }));
    if (index >= 0) updated[index] = { ...updated[index], ...next };
    else updated.push(next);
    onChange(updated);
    setSelectedId("");
    setEnabled(false);
    setUntil("");
  };

  const remove = (id) => onChange(safeItems.filter((item) => String(item?.[idKey] || item?.[type === "warehouse" ? "warehouse_id" : "restaurant_id"] || "") !== String(id)));

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center gap-2"><Icon size={16} className="text-[#d86a78]"/><b className="text-sm text-slate-800">{title}</b><InfoTip text="Overrides are mainly for local exception handling. They can restrict OTP, but they cannot force OTP on when the global master setting is OFF or temporarily paused." /><span className="ml-auto text-[10px] font-bold text-slate-400">{safeItems.length} override(s)</span></div>
      <p className="mt-1 text-[11px] leading-4 text-slate-500">Local OTP exception list.</p>
      {canEdit ? (
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto_auto]">
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold">
            <option value="">Choose {type}</option>
            {options.map((item) => <option key={item.id} value={item.id}>{item.name}{item.city ? ` · ${item.city}` : ""}</option>)}
          </select>
          <select value={enabled ? "required" : "disabled"} onChange={(e) => setEnabled(e.target.value === "required")} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold">
            <option value="disabled">Disable OTP</option>
            <option value="required">Require OTP</option>
          </select>
          <button disabled={!selectedId} onClick={save} className="rounded-xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white disabled:opacity-40">Add / update</button>
          {!enabled ? <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} className="md:col-span-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs" placeholder="Optional temporary disable until"/> : null}
        </div>
      ) : null}
      <div className="mt-3 space-y-2">
        {safeItems.length ? safeItems.map((item) => {
          const id = item?.[idKey] || item?.[type === "warehouse" ? "warehouse_id" : "restaurant_id"];
          const option = options.find((row) => String(row.id) === String(id));
          const isRequired = item?.enabled !== false && globalRequired;
          return <div key={id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5"><div className="min-w-0 flex-1"><b className="block truncate text-xs text-slate-700">{item?.[nameKey] || option?.name || id}</b><span className="text-[10px] text-slate-400">{item?.disabledUntil ? `Disabled until ${dateTime(item.disabledUntil)}` : item?.enabled === false ? "Disabled until manually changed" : "OTP required when global policy allows"}</span></div><Pill tone={isRequired ? "green" : "amber"}>{isRequired ? "OTP" : "Bypass"}</Pill>{canEdit ? <button onClick={() => remove(id)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-400 hover:text-rose-500"><X size={14}/></button> : null}</div>;
        }) : <p className="rounded-xl bg-white px-3 py-3 text-[11px] font-semibold text-slate-400">No local OTP overrides.</p>}
      </div>
    </div>
  );
}

function EffectiveRow({ label, value, text }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3">
      <span className="text-xs font-bold text-slate-500">{label}</span>
      {text !== undefined ? (
        <b className="text-xs text-slate-800">{text}</b>
      ) : value ? (
        <Pill tone="green">Enabled</Pill>
      ) : (
        <Pill tone="slate">Disabled</Pill>
      )}
    </div>
  );
}
function SetupFlow({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#fff1f3] text-[#d86a78]">
        <Icon size={18} />
      </div>
      <div>
        <b className="text-sm text-slate-800">{title}</b>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
    </div>
  );
}

function CollectionDetailModal({
  row,
  events,
  onClose,
  canResolve,
  onResolve,
}) {
  if (!row) return null;
  const review = Boolean(
    collectionValue(row, "duplicatePaymentReview", "duplicate_payment_review"),
  );
  return (
    <Modal
      open
      title={`Collection · ${collectionValue(row, "orderNumber", "order_number") || "Order"}`}
      subtitle="Immutable payment ledger and audit timeline"
      onClose={onClose}
      wide
    >
      <div className="grid gap-4 lg:grid-cols-[.85fr_1.15fr]">
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <MethodPill
                mode={collectionValue(row, "selectedMode", "selected_mode")}
              />
              <Pill
                tone={statusTone(
                  collectionValue(row, "collectionStatus", "collection_status"),
                )}
              >
                {labelize(
                  collectionValue(row, "collectionStatus", "collection_status"),
                )}
              </Pill>
            </div>
            <div className="mt-5 text-3xl font-black tracking-tight text-slate-950">
              {money(collectionValue(row, "amountDue", "amount_due"))}
            </div>
            <p className="mt-1 text-xs font-semibold text-slate-400">
              Customer amount due
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <MiniKV
                label="Cash"
                value={money(collectionValue(row, "cashAmount", "cash_amount"))}
              />
              <MiniKV
                label="Driver UPI"
                value={money(
                  collectionValue(row, "driverUpiAmount", "driver_upi_amount"),
                )}
              />
              <MiniKV
                label="Company Razorpay"
                value={money(
                  collectionValue(
                    row,
                    "companyRazorpayAmount",
                    "company_razorpay_amount",
                  ),
                )}
              />
              <MiniKV
                label="Outstanding"
                value={money(
                  collectionValue(
                    row,
                    "outstandingAmount",
                    "outstanding_amount",
                  ),
                )}
              />
            </div>
          </Card>
          <Card className="p-5">
            <h4 className="text-sm font-black text-slate-800">
              Payment references
            </h4>
            <div className="mt-4 space-y-3 text-xs">
              <DetailLine label="Driver" value={driverTitle(row)} />
              <DetailLine
                label="UPI reference"
                value={
                  collectionValue(
                    row,
                    "driverUpiReference",
                    "driver_upi_reference",
                  ) || "—"
                }
              />
              <DetailLine
                label="UPI ID"
                value={
                  collectionValue(row, "driverUpiId", "driver_upi_id") || "—"
                }
              />
              <DetailLine
                label="Razorpay payment"
                value={
                  collectionValue(
                    row,
                    "razorpayPaymentId",
                    "company_razorpay_payment_id",
                  ) || "—"
                }
              />
              <DetailLine
                label="Settlement status"
                value={labelize(
                  collectionValue(row, "settlementStatus", "settlement_status"),
                )}
              />
            </div>
          </Card>
          {review ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
              <div className="flex gap-2">
                <AlertTriangle size={18} className="shrink-0 text-rose-600" />
                <div>
                  <b className="text-sm text-rose-800">
                    Payment review required
                  </b>
                  <p className="mt-1 text-xs leading-5 text-rose-700">
                    A late/duplicate provider payment was detected after manual
                    collection. Resolve only after finance reconciliation.
                  </p>
                  {canResolve ? (
                    <button
                      onClick={() => onResolve(row)}
                      className="mt-3 rounded-xl bg-rose-600 px-4 py-2 text-xs font-black text-white"
                    >
                      Resolve review
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 p-5">
            <h4 className="text-sm font-black text-slate-800">
              Audit timeline
            </h4>
            <p className="mt-1 text-xs text-slate-400">
              Provider checks, collection confirmation and settlements.
            </p>
          </div>
          <div className="max-h-[570px] overflow-auto p-5">
            {events.length ? (
              <div className="space-y-0">
                {events.map((event, index) => (
                  <div
                    key={event.id || index}
                    className="relative flex gap-4 pb-6 last:pb-0"
                  >
                    <div className="relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#f2c6cc] bg-[#fff8f9] text-[#d86a78]">
                      <Clock3 size={15} />
                    </div>
                    {index < events.length - 1 ? (
                      <div className="absolute left-[17px] top-9 h-[calc(100%-24px)] w-px bg-slate-200" />
                    ) : null}
                    <div className="min-w-0 pt-1">
                      <b className="block text-xs text-slate-800">
                        {labelize(pick(event, "event_type", "eventType"))}
                      </b>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        {pick(event, "event_note", "eventNote") ||
                          pick(event, "payload")?.message ||
                          "Payment ledger event"}
                      </p>
                      <span className="mt-1 block text-[10px] font-semibold text-slate-400">
                        {dateTime(pick(event, "created_at", "createdAt"))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty text="No audit events recorded." />
            )}
          </div>
        </Card>
      </div>
    </Modal>
  );
}
function MiniKV({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <span className="block text-[10px] font-bold uppercase text-slate-400">
        {label}
      </span>
      <b className="mt-1 block break-words text-xs text-slate-800">{value}</b>
    </div>
  );
}
function DetailLine({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-2 last:border-0">
      <span className="text-slate-400">{label}</span>
      <b className="max-w-[60%] break-words text-right text-slate-700">
        {value}
      </b>
    </div>
  );
}

function SettlementDetailModal({
  data,
  onClose,
  canVerify,
  busy,
  onVerify,
  onReject,
}) {
  if (!data?.settlement) return null;
  const s = data.settlement;
  const allocations = Array.isArray(data.allocations) ? data.allocations : [];
  return (
    <Modal
      open
      title="Settlement details"
      subtitle="Driver-to-company settlement allocation"
      onClose={onClose}
      wide
    >
      <div className="grid gap-4 lg:grid-cols-[.8fr_1.2fr]">
        <Card className="p-5">
          <Pill tone={statusTone(s.status)}>{labelize(s.status)}</Pill>
          <div className="mt-4 text-3xl font-black text-slate-950">
            {money(s.amount)}
          </div>
          <p className="mt-1 text-xs text-slate-400">Settlement amount</p>
          <div className="mt-5 space-y-3 text-xs">
            <DetailLine
              label="Driver"
              value={driverTitle({ ...s, driver: data.driver || s.driver })}
            />
            <DetailLine
              label="Method"
              value={labelize(pick(s, "settlement_method", "settlementMethod"))}
            />
            <DetailLine label="Reference" value={s.reference || "—"} />
            <DetailLine
              label="Created"
              value={dateTime(pick(s, "created_at", "createdAt"))}
            />
            <DetailLine label="Note" value={s.note || "—"} />
          </div>
          {canVerify && String(s.status).toUpperCase() === "PENDING" ? (
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                disabled={busy}
                onClick={() => onReject(s)}
                className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-black text-rose-700"
              >
                Reject
              </button>
              <button
                disabled={busy}
                onClick={() => onVerify(s.id)}
                className="rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-black text-white"
              >
                Verify settlement
              </button>
            </div>
          ) : null}
        </Card>
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 p-5">
            <h4 className="text-sm font-black text-slate-800">
              Allocated collections
            </h4>
            <p className="mt-1 text-xs text-slate-400">
              Oldest eligible driver liabilities are reserved/settled first.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-xs">
              <thead className="bg-slate-50 text-slate-400">
                <tr>
                  <th className="px-4 py-3">Collection</th>
                  <th className="px-4 py-3">Allocated</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allocations.length ? (
                  allocations.map((a, i) => (
                    <tr key={a.id || i}>
                      <td className="px-4 py-3 font-bold text-slate-700">
                        {String(
                          pick(a, "collection_id", "collectionId") || "",
                        ).slice(0, 14)}
                      </td>
                      <td className="px-4 py-3 font-black text-slate-900">
                        {money(pick(a, "allocated_amount", "allocatedAmount"))}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {dateTime(pick(a, "created_at", "createdAt"))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">
                      <Empty text="No allocation rows." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Modal>
  );
}

function SettlementFormModal({
  open,
  initialDriver,
  drivers,
  busy,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState({
    driverId: "",
    amount: "",
    settlementMethod: "UPI",
    reference: "",
    proofUrl: "",
    note: "",
  });
  useEffect(() => {
    if (!open) return;
    const id = readDriver(initialDriver || {}).id;
    const available = pick(
      initialDriver,
      "available_to_settle_amount",
      "availableToSettleAmount",
    );
    setForm({
      driverId: id || "",
      amount: available ? String(available) : "",
      settlementMethod: "UPI",
      reference: "",
      proofUrl: "",
      note: "",
    });
  }, [open, initialDriver]);
  return (
    <Modal
      open={open}
      title="Record driver settlement"
      subtitle="This creates an auditable settlement and reserves eligible driver liabilities."
      onClose={onClose}
    >
      <div className="space-y-4">
        <FieldLabel label="Driver">
          <select
            value={form.driverId}
            onChange={(e) =>
              setForm((f) => ({ ...f, driverId: e.target.value }))
            }
            className="input-admin"
          >
            <option value="">Select driver</option>
            {drivers.map((row, i) => {
              const id = readDriver(row).id;
              return id ? (
                <option key={id || i} value={id}>
                  {driverTitle(row)} · due{" "}
                  {money(
                    pick(
                      row,
                      "available_to_settle_amount",
                      "availableToSettleAmount",
                    ),
                  )}
                </option>
              ) : null;
            })}
          </select>
        </FieldLabel>
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldLabel label="Amount">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              className="input-admin"
              placeholder="0.00"
            />
          </FieldLabel>
          <FieldLabel label="Method">
            <select
              value={form.settlementMethod}
              onChange={(e) =>
                setForm((f) => ({ ...f, settlementMethod: e.target.value }))
              }
              className="input-admin"
            >
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="CASH_DEPOSIT">Cash deposit</option>
              <option value="OTHER">Other</option>
            </select>
          </FieldLabel>
        </div>
        <FieldLabel label="Reference / UTR">
          <input
            value={form.reference}
            onChange={(e) =>
              setForm((f) => ({ ...f, reference: e.target.value }))
            }
            className="input-admin"
            placeholder="Transaction reference"
          />
        </FieldLabel>
        <FieldLabel label="Proof URL (optional)">
          <input
            value={form.proofUrl}
            onChange={(e) =>
              setForm((f) => ({ ...f, proofUrl: e.target.value }))
            }
            className="input-admin"
            placeholder="https://..."
          />
        </FieldLabel>
        <FieldLabel label="Note">
          <textarea
            rows="3"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            className="input-admin resize-none"
            placeholder="Settlement note"
          />
        </FieldLabel>
        <button
          disabled={busy || !form.driverId || n(form.amount) <= 0}
          onClick={() => onSubmit(form)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowDownToLine size={16} />
          )}
          Record settlement
        </button>
      </div>
    </Modal>
  );
}
function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-extrabold text-slate-600">
        {label}
      </span>
      {React.cloneElement(children, {
        className: `${children.props.className || ""} w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#fff1f3]`,
      })}
    </label>
  );
}
function RejectModal({ open, busy, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  return (
    <Modal
      open={open}
      title="Reject settlement"
      subtitle="Reserved collection amounts will be released back to outstanding."
      onClose={onClose}
    >
      <FieldLabel label="Reason">
        <textarea
          rows="4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="resize-none"
          placeholder="Why is this settlement rejected?"
        />
      </FieldLabel>
      <button
        disabled={busy}
        onClick={() => onSubmit(reason)}
        className="mt-4 w-full rounded-xl bg-rose-600 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
      >
        Reject settlement
      </button>
    </Modal>
  );
}
function ReviewModal({ open, busy, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  useEffect(() => {
    if (open) {
      setNote("");
      setReference("");
    }
  }, [open]);
  return (
    <Modal
      open={open}
      title="Resolve payment reconciliation"
      subtitle="This only marks the review resolved. It does not refund or move bank money."
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-semibold leading-5 text-amber-800">
          Confirm the Razorpay payment, customer collection and any required
          refund outside this action before resolving.
        </div>
        <FieldLabel label="Resolution note">
          <textarea
            rows="4"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="resize-none"
            placeholder="What was checked and how was it resolved?"
          />
        </FieldLabel>
        <FieldLabel label="Reference (optional)">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Refund / support / finance reference"
          />
        </FieldLabel>
        <button
          disabled={busy || note.trim().length < 3}
          onClick={() =>
            onSubmit({ note: note.trim(), reference: reference.trim() })
          }
          className="w-full rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
        >
          Mark review resolved
        </button>
      </div>
    </Modal>
  );
}
